import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  Campaign,
  deleteCampaign,
  duplicateCampaign,
  getCampaigns,
  saveCampaignAsTemplate,
} from "../../../helpers/campaigns_helper";
import TestCampaignModal from "./TestCampaignModal";
import {
  BillingOperationalEmpty,
  isBillingSoftLocked,
} from "../../../Components/Billing/BillingSoftLockGuard";
import { getSubscription } from "../../../helpers/subscription_helper";

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 350;

/** Coluna de ações gruda à direita no scroll horizontal (mobile). */
const stickyActionsStyle: React.CSSProperties = {
  position: "sticky",
  right: 0,
  background: "var(--vz-secondary-bg, var(--bs-body-bg, #fff))",
  boxShadow: "-6px 0 8px -6px rgba(0,0,0,.12)",
  zIndex: 1,
};

const stickyActionsHeadStyle: React.CSSProperties = {
  ...stickyActionsStyle,
  background: "var(--vz-table-header-bg, #f3f6f9)",
};

const campaignHasHtml = (campaign: Campaign): boolean =>
  Boolean(campaign.html_body?.trim());

type HtmlFilter = "all" | "with" | "without";
type LegacyFilter = "all" | "legacy" | "native";

/**
 * Lista de campanhas do tenant (API /api/v1/campaigns).
 * Ícones + Nome/assunto; sticky ações; preview HTML; filtro com/sem conteúdo.
 */
const CampaignsList = () => {
  const { t, i18n } = useTranslation();
  document.title = t("campaigns.documentTitle");

  const helpHref = helpUrlForRoute("/campaigns", i18n.language);
  const [searchParams, setSearchParams] = useSearchParams();

  const htmlFromUrl = searchParams.get("has_html");
  const legacyFromUrl =
    searchParams.get("imported_from_legacy") ?? searchParams.get("legacy");

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [htmlFilter, setHtmlFilter] = useState<HtmlFilter>(() => {
    if (htmlFromUrl === "1" || htmlFromUrl === "true") return "with";
    if (htmlFromUrl === "0" || htmlFromUrl === "false") return "without";
    return "all";
  });
  const [legacyFilter, setLegacyFilter] = useState<LegacyFilter>(() => {
    if (legacyFromUrl === "1" || legacyFromUrl === "true") return "legacy";
    if (legacyFromUrl === "0" || legacyFromUrl === "false") return "native";
    return "all";
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const [saveAsTemplate, setSaveAsTemplate] = useState<Campaign | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateNameError, setTemplateNameError] = useState<string | null>(
    null
  );
  const [savingTemplateBusy, setSavingTemplateBusy] = useState(false);

  const [testCampaign, setTestCampaign] = useState<Campaign | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  /** Índice focado para atalhos e/t/d (LAC-114) — -1 = nenhum. */
  const [focusIndex, setFocusIndex] = useState(-1);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const navigate = useNavigate();
  // Cinto de segurança: empty se soft-lock falhar no guard.
  const [billingLockStatus, setBillingLockStatus] = useState<string | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;
    getSubscription()
      .then((res) => {
        const status = res.data?.status;
        if (!cancelled && isBillingSoftLocked(status)) {
          setBillingLockStatus(status || "suspended");
        }
      })
      .catch(() => {
        /* API já redireciona 403 */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasActiveFilters =
    Boolean(appliedSearch) || htmlFilter !== "all" || legacyFilter !== "all";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCampaigns({
        search: appliedSearch || undefined,
        has_html:
          htmlFilter === "all"
            ? undefined
            : htmlFilter === "with",
        imported_from_legacy:
          legacyFilter === "all"
            ? undefined
            : legacyFilter === "legacy",
        page,
        per_page: PER_PAGE,
      });
      if (!res?.success) {
        setError(t("campaigns.error.load"));
        setCampaigns([]);
        return;
      }
      setCampaigns(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("campaigns.error.load"));
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, htmlFilter, legacyFilter, page, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    setError(null);
    try {
      const res = await deleteCampaign(deleting.id);
      toast.success(res?.message || t("campaigns.toast.deleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("campaigns.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const onDuplicate = async (campaign: Campaign) => {
    setDuplicatingId(campaign.id);
    setError(null);
    try {
      const res = await duplicateCampaign(campaign.id);
      toast.success(res?.message || t("campaigns.toast.duplicated"), {
        autoClose: 2500,
      });
      await load();
    } catch (err: any) {
      setError(err?.message || t("campaigns.error.duplicate"));
    } finally {
      setDuplicatingId(null);
    }
  };

  const openSaveAsTemplate = (campaign: Campaign) => {
    setSaveAsTemplate(campaign);
    setTemplateName(campaign.name);
    setTemplateNameError(null);
  };

  const closeSaveAsTemplate = () => {
    if (savingTemplateBusy) {
      return;
    }
    setSaveAsTemplate(null);
    setTemplateName("");
    setTemplateNameError(null);
  };

  const confirmSaveAsTemplate = async () => {
    if (!saveAsTemplate) {
      return;
    }
    const name = templateName.trim();
    if (!name) {
      setTemplateNameError(t("campaigns.saveAsTemplate.nameRequired"));
      return;
    }
    if (name.length > 255) {
      setTemplateNameError(t("campaigns.saveAsTemplate.nameMax"));
      return;
    }

    setSavingTemplateBusy(true);
    setError(null);
    setTemplateNameError(null);
    try {
      const res = await saveCampaignAsTemplate(saveAsTemplate.id, { name });
      const templateId = res?.data?.id;
      toast.success(
        <span>
          {res?.message || t("campaigns.toast.savedAsTemplate")}{" "}
          {templateId ? (
            <Link
              to={`/templates/${templateId}/edit`}
              className="text-decoration-underline fw-semibold"
            >
              {t("campaigns.toast.openTemplate")}
            </Link>
          ) : null}
        </span>,
        { autoClose: 6000 }
      );
      setSaveAsTemplate(null);
      setTemplateName("");
    } catch (err: any) {
      setError(err?.message || t("campaigns.error.saveAsTemplate"));
      setSaveAsTemplate(null);
    } finally {
      setSavingTemplateBusy(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setHtmlFilter("all");
    setLegacyFilter("all");
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  // Deep-link: ?has_html=1&imported_from_legacy=1 (ou legacy=1).
  useEffect(() => {
    const next = new URLSearchParams();
    if (htmlFilter === "with") next.set("has_html", "1");
    if (htmlFilter === "without") next.set("has_html", "0");
    if (legacyFilter === "legacy") next.set("imported_from_legacy", "1");
    if (legacyFilter === "native") next.set("imported_from_legacy", "0");
    const current = searchParams.toString();
    const desired = next.toString();
    if (current !== desired) {
      setSearchParams(next, { replace: true });
    }
  }, [htmlFilter, legacyFilter, searchParams, setSearchParams]);

  const openPreview = (campaign: Campaign) => {
    if (!campaignHasHtml(campaign)) {
      toast.info(t("campaigns.preview.empty"), { autoClose: 3000 });
      return;
    }
    setPreviewCampaign(campaign);
  };

  // Atalhos na lista: e=editar, t=teste, d=duplicar, p=preview, j/k navega (LAC-114).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        document.getElementById("campaigns-search")?.focus();
        return;
      }
      // "?" (Shift+/) — cheatsheet de atalhos.
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        e.preventDefault();
        setShortcutsHelpOpen(true);
        return;
      }
      if (campaigns.length === 0) {
        return;
      }
      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((i) =>
          Math.min(campaigns.length - 1, Math.max(0, i) + (i < 0 ? 0 : 1))
        );
        return;
      }
      if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
        return;
      }
      const idx = focusIndex < 0 ? 0 : focusIndex;
      const campaign = campaigns[idx];
      if (!campaign) {
        return;
      }
      if (e.key === "e" || e.key === "Enter") {
        e.preventDefault();
        navigate(`/campaigns/${campaign.id}/edit`);
      } else if (e.key === "t") {
        e.preventDefault();
        setTestCampaign(campaign);
      } else if (e.key === "d") {
        e.preventDefault();
        void onDuplicate(campaign);
      } else if (e.key === "p") {
        e.preventDefault();
        openPreview(campaign);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [campaigns, focusIndex, navigate, t]);

  if (billingLockStatus) {
    return <BillingOperationalEmpty status={billingLockStatus} />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("campaigns.title")}
          actions={
            <PageHelpLink href={helpHref} label={t("campaigns.help")} />
          }
        />

        <ToastContainer closeButton={false} limit={1} />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 flex-grow-1 align-items-center">
                  <div style={{ maxWidth: 320, flex: "1 1 220px" }}>
                    <Input
                      id="campaigns-search"
                      type="search"
                      placeholder={t("campaigns.search.placeholder")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label={t("campaigns.search.placeholder")}
                    />
                  </div>
                  <Input
                    type="select"
                    style={{ maxWidth: 200 }}
                    value={htmlFilter}
                    onChange={(e) => {
                      setHtmlFilter(e.target.value as HtmlFilter);
                      setPage(1);
                    }}
                    aria-label={t("campaigns.filter.html.label")}
                  >
                    <option value="all">
                      {t("campaigns.filter.html.all")}
                    </option>
                    <option value="with">
                      {t("campaigns.filter.html.with")}
                    </option>
                    <option value="without">
                      {t("campaigns.filter.html.without")}
                    </option>
                  </Input>
                  <Input
                    type="select"
                    style={{ maxWidth: 220 }}
                    value={legacyFilter}
                    onChange={(e) => {
                      setLegacyFilter(e.target.value as LegacyFilter);
                      setPage(1);
                    }}
                    aria-label={t("campaigns.filter.legacy.label")}
                  >
                    <option value="all">
                      {t("campaigns.filter.legacy.all")}
                    </option>
                    <option value="legacy">
                      {t("campaigns.filter.legacy.legacy")}
                    </option>
                    <option value="native">
                      {t("campaigns.filter.legacy.native")}
                    </option>
                  </Input>
                  {hasActiveFilters && (
                    <Button
                      color="link"
                      className="text-decoration-underline p-0"
                      onClick={clearFilters}
                    >
                      {t("campaigns.filter.clear")}
                    </Button>
                  )}
                </div>
                <Button color="primary" tag={Link} to="/campaigns/new">
                  {t("campaigns.create")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : campaigns.length === 0 ? (
                  <div className="text-center py-5">
                    {!hasActiveFilters && (
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                          <i className="ri-mail-send-line" />
                        </div>
                      </div>
                    )}
                    <h5 className="mb-2">
                      {legacyFilter === "legacy" && hasActiveFilters
                        ? t("campaigns.empty.legacy.title")
                        : hasActiveFilters
                          ? t("campaigns.empty.filtered.title")
                          : t("campaigns.empty.title")}
                    </h5>
                    <p className="text-muted mb-3">
                      {legacyFilter === "legacy" && hasActiveFilters
                        ? t("campaigns.empty.legacy.description")
                        : hasActiveFilters
                          ? t("campaigns.empty.filtered.description")
                          : t("campaigns.empty.description")}
                    </p>
                    {!hasActiveFilters && (
                      <p className="text-muted small mb-3">
                        {t("campaigns.empty.templatesHint")}
                      </p>
                    )}
                    {hasActiveFilters ? (
                      <Button color="light" onClick={clearFilters}>
                        {t("campaigns.filter.clear")}
                      </Button>
                    ) : (
                      <div className="d-flex flex-column align-items-center gap-2">
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                          <Button color="primary" tag={Link} to="/campaigns/new">
                            {t("campaigns.empty.cta")}
                          </Button>
                          <Button
                            color="soft-primary"
                            tag={Link}
                            to="/templates"
                          >
                            {t("campaigns.empty.useTemplate")}
                          </Button>
                          <Button
                            color="soft-success"
                            tag={Link}
                            to="/templates/gallery"
                          >
                            {t("campaigns.empty.usePlatformTemplate")}
                          </Button>
                        </div>
                        <a
                          href={helpUrlForRoute("/campaigns/new", i18n.language)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link"
                        >
                          {t("campaigns.empty.help")}
                          <i className="ri-external-link-line ms-1 align-middle" />
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-2">
                      {t("campaigns.totalCount", { count: total })}
                    </p>
                    <p className="text-muted small mb-3 d-flex flex-wrap align-items-center gap-2">
                      <span>{t("campaigns.shortcuts.hint")}</span>
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 align-baseline"
                        type="button"
                        onClick={() => setShortcutsHelpOpen(true)}
                        title={t("campaigns.shortcuts.helpTitle")}
                        aria-label={t("campaigns.shortcuts.helpTitle")}
                      >
                        <i className="ri-question-line me-1" aria-hidden />
                        {t("campaigns.shortcuts.helpLink")}
                      </Button>
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("campaigns.table.nameSubject")}</th>
                            <th className="text-nowrap" style={{ width: 160 }}>
                              {t("campaigns.table.updatedAt")}
                            </th>
                            <th
                              className="text-end text-nowrap"
                              style={{ width: 180, ...stickyActionsHeadStyle }}
                              aria-label={t("campaigns.table.actions")}
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {campaigns.map((campaign, rowIndex) => {
                            const hasHtml = campaignHasHtml(campaign);
                            const focused = focusIndex === rowIndex;
                            return (
                              <tr
                                key={campaign.id}
                                className={focused ? "table-active" : undefined}
                                onClick={() => setFocusIndex(rowIndex)}
                              >
                                <td>
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <Link
                                      to={`/campaigns/${campaign.id}/edit`}
                                      className="fw-medium text-body text-decoration-none"
                                      title={t("campaigns.edit.button")}
                                    >
                                      {campaign.name}
                                    </Link>
                                    {!hasHtml && (
                                      <Badge
                                        color="warning"
                                        pill
                                        className="fw-normal"
                                        title={t("campaigns.badge.noHtmlTitle")}
                                      >
                                        {t("campaigns.badge.noHtml")}
                                      </Badge>
                                    )}
                                    {campaign.imported_from_legacy ? (
                                      <Badge
                                        color="secondary"
                                        pill
                                        className="fw-normal"
                                        title={t(
                                          "campaigns.badge.legacyTitle"
                                        )}
                                      >
                                        {t("campaigns.badge.legacy")}
                                      </Badge>
                                    ) : null}
                                  </div>
                                  <small className="text-muted">
                                    {campaign.subject || "—"}
                                  </small>
                                </td>
                                <td className="text-muted text-nowrap">
                                  {campaign.updated_at
                                    ? new Date(
                                        campaign.updated_at
                                      ).toLocaleString()
                                    : "—"}
                                </td>
                                <td
                                  className="text-end text-nowrap"
                                  style={stickyActionsStyle}
                                >
                                  <Button
                                    color="soft-info"
                                    size="sm"
                                    className="btn-icon"
                                    onClick={() => openPreview(campaign)}
                                    title={t("campaigns.preview.button")}
                                    aria-label={t("campaigns.preview.button")}
                                  >
                                    <i className="ri-eye-line" aria-hidden />
                                  </Button>

                                  {/* Disparo: ícone abre Enviar campanha + Enviar teste */}
                                  <UncontrolledDropdown className="d-inline-block ms-1">
                                    <DropdownToggle
                                      tag="button"
                                      type="button"
                                      className="btn btn-soft-success btn-sm btn-icon"
                                      title={t("campaigns.actions.sendTitle")}
                                      aria-label={t(
                                        "campaigns.actions.sendTitle"
                                      )}
                                    >
                                      <i
                                        className="ri-send-plane-fill"
                                        aria-hidden
                                      />
                                    </DropdownToggle>
                                    <DropdownMenu end>
                                      <DropdownItem
                                        tag={Link}
                                        to={`/campaigns/${campaign.id}/send`}
                                      >
                                        <i
                                          className="ri-mail-send-line me-2 text-muted"
                                          aria-hidden
                                        />
                                        {t("campaigns.send.campaign")}
                                      </DropdownItem>
                                      <DropdownItem
                                        onClick={() =>
                                          setTestCampaign(campaign)
                                        }
                                      >
                                        <i
                                          className="ri-flask-line me-2 text-muted"
                                          aria-hidden
                                        />
                                        {t("campaigns.test.button")}
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>

                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="btn-icon ms-1"
                                    tag={Link}
                                    to={`/campaigns/${campaign.id}/edit`}
                                    title={t("campaigns.edit.button")}
                                    aria-label={t("campaigns.edit.button")}
                                  >
                                    <i
                                      className="ri-pencil-fill"
                                      aria-hidden
                                    />
                                  </Button>

                                  <Button
                                    color="soft-secondary"
                                    size="sm"
                                    className="btn-icon ms-1"
                                    disabled={duplicatingId === campaign.id}
                                    onClick={() => onDuplicate(campaign)}
                                    title={t("campaigns.duplicate.button")}
                                    aria-label={t(
                                      "campaigns.duplicate.button"
                                    )}
                                  >
                                    {duplicatingId === campaign.id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <i
                                        className="ri-file-copy-line"
                                        aria-hidden
                                      />
                                    )}
                                  </Button>

                                  {/* Mais (:): salvar como modelo + excluir */}
                                  <UncontrolledDropdown className="d-inline-block ms-1">
                                    <DropdownToggle
                                      tag="button"
                                      type="button"
                                      className="btn btn-soft-secondary btn-sm btn-icon fw-bold"
                                      title={t("campaigns.actions.more")}
                                      aria-label={t("campaigns.actions.more")}
                                    >
                                      <span aria-hidden>:</span>
                                    </DropdownToggle>
                                    <DropdownMenu end>
                                      <DropdownItem
                                        onClick={() =>
                                          openSaveAsTemplate(campaign)
                                        }
                                      >
                                        <i
                                          className="ri-layout-5-line me-2 text-muted"
                                          aria-hidden
                                        />
                                        {t(
                                          "campaigns.saveAsTemplate.button"
                                        )}
                                      </DropdownItem>
                                      <DropdownItem divider />
                                      <DropdownItem
                                        className="text-danger"
                                        onClick={() => setDeleting(campaign)}
                                      >
                                        <i
                                          className="ri-delete-bin-line me-2"
                                          aria-hidden
                                        />
                                        {t("campaigns.delete.button")}
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 && (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("campaigns.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("campaigns.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("campaigns.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={shortcutsHelpOpen}
        toggle={() => setShortcutsHelpOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setShortcutsHelpOpen(false)}>
          {t("campaigns.shortcuts.helpTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">
            {t("campaigns.shortcuts.helpIntro")}
          </p>
          <Table size="sm" borderless className="mb-0">
            <tbody>
              {(
                [
                  ["j / ↓", "campaigns.shortcuts.keys.next"],
                  ["k / ↑", "campaigns.shortcuts.keys.prev"],
                  ["e / Enter", "campaigns.shortcuts.keys.edit"],
                  ["t", "campaigns.shortcuts.keys.test"],
                  ["d", "campaigns.shortcuts.keys.duplicate"],
                  ["p", "campaigns.shortcuts.keys.preview"],
                  ["/", "campaigns.shortcuts.keys.search"],
                  ["?", "campaigns.shortcuts.keys.thisHelp"],
                ] as const
              ).map(([keys, labelKey]) => (
                <tr key={keys}>
                  <td className="text-nowrap pe-3">
                    <kbd>{keys}</kbd>
                  </td>
                  <td>{t(labelKey)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setShortcutsHelpOpen(false)}>
            {t("campaigns.shortcuts.helpClose")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("campaigns.delete.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {t("campaigns.delete.confirm", { name: deleting?.name })}
          </p>
          <p className="text-muted mb-0 small">
            {t("campaigns.delete.consequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("campaigns.form.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={confirmDelete}
          >
            {deletingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("campaigns.delete.confirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!saveAsTemplate}
        toggle={closeSaveAsTemplate}
        centered
      >
        <ModalHeader toggle={closeSaveAsTemplate}>
          {t("campaigns.saveAsTemplate.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            {t("campaigns.saveAsTemplate.help", {
              name: saveAsTemplate?.name,
            })}
          </p>
          <Label htmlFor="save-as-template-name">
            {t("campaigns.saveAsTemplate.nameLabel")}
          </Label>
          <Input
            id="save-as-template-name"
            value={templateName}
            onChange={(e) => {
              setTemplateName(e.target.value);
              if (templateNameError) {
                setTemplateNameError(null);
              }
            }}
            invalid={Boolean(templateNameError)}
            disabled={savingTemplateBusy}
            placeholder={t("campaigns.saveAsTemplate.namePlaceholder")}
          />
          {templateNameError && (
            <FormFeedback className="d-block">{templateNameError}</FormFeedback>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={savingTemplateBusy}
            onClick={closeSaveAsTemplate}
          >
            {t("campaigns.form.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={savingTemplateBusy}
            onClick={confirmSaveAsTemplate}
          >
            {savingTemplateBusy ? (
              <Spinner size="sm" />
            ) : (
              t("campaigns.saveAsTemplate.confirm")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Preview rápido — sandbox isola scripts do HTML da campanha */}
      <Modal
        isOpen={!!previewCampaign}
        toggle={() => setPreviewCampaign(null)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setPreviewCampaign(null)}>
          {t("campaigns.preview.title", { name: previewCampaign?.name })}
        </ModalHeader>
        <ModalBody className="p-0">
          {previewCampaign?.subject ? (
            <p className="px-3 pt-3 mb-2 text-muted small">
              {t("campaigns.preview.subject", {
                subject: previewCampaign.subject,
              })}
            </p>
          ) : null}
          <iframe
            title={t("campaigns.preview.iframeTitle")}
            srcDoc={previewCampaign?.html_body || ""}
            sandbox=""
            className="w-100 border-0"
            style={{ minHeight: 420, background: "#fff" }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPreviewCampaign(null)}>
            {t("campaigns.form.cancel")}
          </Button>
          {previewCampaign && (
            <>
              <Button
                color="soft-primary"
                tag={Link}
                to={`/campaigns/${previewCampaign.id}/edit`}
              >
                {t("campaigns.edit.button")}
              </Button>
              <Button
                color="success"
                tag={Link}
                to={`/campaigns/${previewCampaign.id}/send`}
              >
                {t("campaigns.send.campaign")}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      <TestCampaignModal
        campaign={testCampaign}
        isOpen={!!testCampaign}
        onClose={() => setTestCampaign(null)}
      />
    </div>
  );
};

export default CampaignsList;
